import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Check, CheckCheck, X, AlertTriangle, CircleX, RefreshCw } from "lucide-react";
import { useTranslation } from "@/shared/i18n/I18nProvider";
import {
  useNotifications,
  useReadAllNotifications,
  useReadNotification,
  type NotificationRow,
  type NotificationType,
} from "@/shared/api/queries";
import { log } from "@/shared/logger";
import { color, withAlpha } from "@/shared/tokens";

/**
 * NotificationBell — header bell icon + side drawer for the inbox.
 *
 * Spec: `document/design/iSale Notifications.html` lines 2261+.
 *   - Bell icon with unread count badge (top-right)
 *   - Click opens a right-slide 320px drawer with up to 20 items
 *   - Rows: variant icon + unread dot + title + relative time
 *   - "Đọc hết" link at top right of drawer
 *   - 4 variants from backend NotificationType → colour:
 *     render_done=success, render_failed=error, quota_warn=warning,
 *     payment_failed=error
 *
 * Polls `/v1/notifications?unread_only=true&limit=20` every 30s via the
 * `useNotifications()` hook so the badge stays fresh without WebSockets.
 */
export function NotificationBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Use unread_only=false so the drawer can show both unread + recent-read
  // (matches mobile pattern). Badge count still uses `unread_count`.
  const list = useNotifications({ unreadOnly: false, limit: 20 });
  const readOne = useReadNotification();
  const readAll = useReadAllNotifications();
  const unread = list.data?.unread_count ?? 0;

  // Close drawer on Esc
  useEffect(() => {
    if (!open) return;
    function onKey(ev: KeyboardEvent) {
      if (ev.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function handleOpen() {
    log.route("notification-bell open", { unread });
    setOpen(true);
  }
  function handleRowClick(row: NotificationRow) {
    if (!row.read_at) {
      void readOne.mutateAsync({ id: row.id }).catch(() => {});
    }
    if (row.deep_link) {
      // Internal deep-link only — never navigate to external URLs.
      window.location.href = row.deep_link.startsWith("/") ? row.deep_link : "/dashboard";
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label={t("notifications.bellLabel")}
        className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-black/5"
      >
        <Bell className="h-4 w-4 text-fg-muted" />
        {unread > 0 && (
          <span
            className="absolute top-1 right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full border-2 border-white px-1 text-4xs font-extrabold leading-none text-white" /* layout-ok: one-off component dimension */
            style={{ backgroundColor: color.error }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Portal to document.body so the drawer + backdrop sit ABOVE the
          MobileBottomTab (`z-20`) and the header sticky chrome (`z-10`)
          without inheriting any parent `transform` stacking context that
          would clip `fixed inset-0`. */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <>
                <motion.div
                  key="backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                  onClick={() => setOpen(false)}
                  aria-hidden
                />
                <motion.aside
                  key="drawer"
                  role="dialog"
                  aria-labelledby="notif-drawer-title"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{
                    type: "spring",
                    stiffness: 320,
                    damping: 32,
                  }}
                  className="fixed top-0 right-0 z-50 flex h-dvh w-full flex-col border-l border-default bg-elevated shadow-2xl md:w-[360px]" /* layout-ok: one-off component dimension */
                >
            <header className="flex items-center justify-between border-b border-default px-4 py-3">
              <h3
                id="notif-drawer-title"
                className="text-sm font-extrabold text-fg"
              >
                {t("notifications.drawerTitle", { count: unread })}
              </h3>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      void readAll.mutateAsync().catch(() => {});
                    }}
                    className="rounded-md px-2 py-1 text-2xs font-extrabold text-primary transition-colors hover:bg-black/5"
                  >
                    <CheckCheck className="mr-1 inline h-3 w-3" />
                    {t("notifications.readAll")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t("common.close")}
                  className="rounded-full p-1 hover:bg-black/5"
                >
                  <X className="h-4 w-4 text-fg-subtle" />
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-y-auto">
              {list.isLoading && (
                <div className="p-6 text-center text-xs text-fg-subtle">
                  {t("notifications.loading")}
                </div>
              )}
              {list.isError && (
                <div className="p-6 text-center text-xs text-danger">
                  {t("notifications.loadError")}
                </div>
              )}
              {list.data && list.data.items.length === 0 && (
                <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
                  <Bell className="h-8 w-8 text-fg-subtle" />
                  <div className="text-sm font-bold text-fg">
                    {t("notifications.emptyTitle")}
                  </div>
                  <p className="max-w-[200px] text-xs text-fg-subtle" /* layout-ok: one-off component dimension */>
                    {t("notifications.emptyBody")}
                  </p>
                </div>
              )}
              {list.data && list.data.items.length > 0 && (
                <ul>
                  {list.data.items.map((row) => (
                    <NotificationRowItem
                      key={row.id}
                      row={row}
                      onClick={() => handleRowClick(row)}
                    />
                  ))}
                </ul>
              )}
            </div>
                </motion.aside>
              </>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}

function NotificationRowItem({
  row,
  onClick,
}: {
  row: NotificationRow;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const variant = variantOf(row.type);
  const Icon = iconOf(row.type);
  const unread = !row.read_at;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-start gap-3 border-b border-subtle px-4 py-3 text-left transition-colors hover:bg-black/[0.02]" /* layout-ok: one-off component dimension */
      >
        {/* Unread dot */}
        <span
          className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: unread ? variant.color : "transparent" }}
        />
        {/* Icon chip */}
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: variant.bg }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color: variant.color }} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="text-cap leading-tight text-fg"
            style={{
              fontWeight: unread ? 700 : 500,
            }}
          >
            {row.title}
          </div>
          {row.body && (
            <div
              className="mt-0.5 line-clamp-2 text-2xs text-fg-subtle"
            >
              {row.body}
            </div>
          )}
          <div className="mt-1 text-3xs text-fg-subtle">
            {formatRelative(row.created_at, t)}
          </div>
        </div>
      </button>
    </li>
  );
}

// ───────── Helpers ─────────

function variantOf(type: NotificationType): { color: string; bg: string } {
  switch (type) {
    case "render_done":
      return { color: color.success, bg: withAlpha(color.success, 0.1) };
    case "render_failed":
    case "payment_failed":
      return { color: color.error, bg: withAlpha(color.error, 0.1) };
    case "quota_warn":
      return { color: color.warn, bg: withAlpha(color.warn, 0.1) };
    default:
      return { color: color.indigo[500], bg: withAlpha(color.indigo[500], 0.1) };
  }
}

function iconOf(type: NotificationType) {
  switch (type) {
    case "render_done":
      return Check;
    case "render_failed":
      return CircleX;
    case "quota_warn":
      return AlertTriangle;
    case "payment_failed":
      return RefreshCw;
    default:
      return Bell;
  }
}

function formatRelative(
  iso: string,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  const created = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - created) / 1000));
  if (diffSec < 60) return t("notifications.relativeNow");
  if (diffSec < 3600)
    return t("notifications.relativeMin", { n: Math.floor(diffSec / 60) });
  if (diffSec < 86400)
    return t("notifications.relativeHour", { n: Math.floor(diffSec / 3600) });
  if (diffSec < 86400 * 7)
    return t("notifications.relativeDay", { n: Math.floor(diffSec / 86400) });
  return new Date(iso).toLocaleDateString();
}
